/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import '../../utils/device-identifier.js'; // just to pull in modrnzr
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import { KEYCODE } from '../../utils/keycode.js';
import buttonStyles from './sh-button.lit.scss.js';

/**
 * @prop {String} label - Defines the button's label in all shapes (in circle shape it will only be shown if tooltip is also defined).
 * @prop {String} icon - Defines the button's icon. Only applies to circle shape.
 * @prop {String} color - Defines the color of the button. Possible values are `primary`, `secondary`, `tertiary`.
 * @prop {String} size - If icon is not set, defines the size of the button. Possible values are `s`, `m`, `l`, `auto` (in which size grows based on label's length with `l` size being the maximum viable width), `max-content` (in which size grows based on label's length without having a maximum width) and `full-width` (in which size covers the whole width of the parent container independent of label's length. This variation should be used for mobile's use case only).
 * @attr {String} shape - Defines the shape of the button. Possible values are `pill-s`, `pill-m`, `pill-l`, `circle`.
 * @prop {Boolean} disabled - If set, won't allow mouse events and disabled styles will be applied to the button.
 * @prop {String} href - Defines a path to which navigation will occur on click.
 * @prop {String} tooltip - Defines the position of the tooltip (if an icon and label are set).
 */
export class SHButton extends ControlStateMixin(LitElement) {
  static get properties() {
    return {
      icon: { type: String, reflect: true },
      label: { type: String, reflect: true },
      size: { type: String, reflect: true },
      color: { type: String, reflect: true },
      tooltip: { type: String, reflect: true },
      href: { type: String, reflect: true },
      /** @ignore */
      role: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [extendInteractiveArea(), buttonStyles];
  }

  render() {
    return html`
      ${!this.icon || (this.getAttribute('shape') && this.getAttribute('shape').includes('pill'))
        ? html` <div class="label">${this.label}</div> `
        : html`
            <sh-icon icon="${this.icon}"></sh-icon>
            ${this.tooltip ? html` <div class="tooltip ${this.tooltip}">${this.label}</div> ` : ''}
          `}
    `;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('href') && this.href) {
      this.addHrefListener();
    }
    const listOfProperties = ['icon', 'label', 'size', 'color', 'tooltip', 'href'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  constructor() {
    super();
    this.label = 'Label';
    this.size = 's';
    this.color = 'primary';
    this.role = 'button';
  }

  firstUpdated() {
    super.firstUpdated();
    if (!Modernizr.hovermq) {
      this.classList.add('no-hovermq');
    }
    this.addEventListener('focus', () => this.handleFocus());
    this.addEventListener('blur', () => this.handleBlur());
    if (this.hasAttribute('shape')) {
      this.handleLegacyAttributes();
    }
  }

  addHrefListener() {
    this.addEventListener('click', () => {
      window.location.href = this.href;
    });
  }

  handleLegacyAttributes() {
    const shape = this.getAttribute('shape');
    if (shape == 'pill-s') {
      this.size = 's';
    } else if (shape == 'pill-m') {
      this.size = 'm';
    } else if (shape == 'pill-l') {
      this.size = 'l';
    } else if (shape == 'circle') {
      if (!this.icon) {
        // legacy default icon property value
        this.icon = 'save';
      }
    }
    console.warn(
      `You are using a legacy property (${shape}) of sh-button component. Please use the new 'size' property instead.`
    );
  }

  handleFocus() {
    this.addEventListener('keyup', this.handleKeyboard);
  }

  handleKeyboard(e) {
    let self;
    self = this;
    if (e.keyCode === KEYCODE.ENTER || e.keyCode === KEYCODE.SPACE) {
      self.click();
    }
    if (e.code === 'Tab') {
      self.style.outline = 'var(--focus-outline)';
      self.style.outlineOffset = '-0.125rem';
      const tooltip = self.shadowRoot.querySelector('.tooltip');
      if (tooltip) {
        tooltip.style.opacity = 1;
        tooltip.style.visibility = 'visible';
      }
    }
  }

  handleBlur() {
    const tooltip = this.shadowRoot.querySelector('.tooltip');
    this.style.outline = '';
    this.style.outlineOffset = '';
    this.removeEventListener('keyup', this.handleKeyboard);
    if (tooltip) {
      tooltip.style.opacity = '';
      tooltip.style.visibility = '';
    }
  }
}

customElements.define('sh-button', SHButton);
